import { KSTransaction } from './types';

interface VendorPeriod {
  vendor: number;
  startDate: Date;
  endDate: Date;
  expectedFrequency: number; // transactions per month
  actualTransactions: KSTransaction[];
  status: 'active' | 'inactive' | 'interrupted';
  confidenceScore: number;
}

interface VendorLifecycleAnomaly {
  vendor: number;
  anomalyType: 'start' | 'stop' | 'interruption' | 'frequency_change';
  date: Date;
  details: {
    previousPattern?: {
      frequency: number;
      typicalGap: number;
    };
    newPattern?: {
      frequency: number;
      typicalGap: number;
    };
    confidenceScore: number;
  };
}

/**
 * Detects the active periods for each vendor
 */
export function detectVendorPeriods(
  transactions: KSTransaction[],
  minTransactions: number = 3,
  maxGapDays: number = 45 // Consider inactive if no transaction for 45 days
): VendorPeriod[] {
  // Group transactions by vendor
  const vendorTransactions = new Map<number, KSTransaction[]>();
  transactions.forEach(txn => {
    if (!vendorTransactions.has(txn.vendorId)) {
      vendorTransactions.set(txn.vendorId, []);
    }
    vendorTransactions.get(txn.vendorId)!.push(txn);
  });

  const periods: VendorPeriod[] = [];

  vendorTransactions.forEach((vendorTxns, vendor) => {
    if (vendorTxns.length < minTransactions) {
      return; // Skip vendors with too few transactions
    }

    // Sort transactions by date
    const sortedTxns = [...vendorTxns].sort((a, b) => a.date.getTime() - b.date.getTime());

    let currentPeriod: {
      startDate: Date;
      transactions: KSTransaction[];
    } | null = null;

    sortedTxns.forEach((txn, index) => {
      if (!currentPeriod) {
        currentPeriod = {
          startDate: txn.date,
          transactions: [txn],
        };
        return;
      }

      const daysSinceLastTxn =
        (txn.date.getTime() - currentPeriod.transactions[currentPeriod.transactions.length - 1].date.getTime()) /
        (1000 * 60 * 60 * 24);

      if (daysSinceLastTxn > maxGapDays) {
        // End current period and start new one
        if (currentPeriod.transactions.length >= minTransactions) {
          periods.push(
            createVendorPeriod(
              vendor,
              currentPeriod.startDate,
              currentPeriod.transactions[currentPeriod.transactions.length - 1].date,
              currentPeriod.transactions
            )
          );
        }
        currentPeriod = {
          startDate: txn.date,
          transactions: [txn],
        };
      } else {
        currentPeriod.transactions.push(txn);
      }

      // Handle last period
      if (index === sortedTxns.length - 1 && currentPeriod.transactions.length >= minTransactions) {
        periods.push(createVendorPeriod(vendor, currentPeriod.startDate, txn.date, currentPeriod.transactions));
      }
    });
  });

  return periods;
}

/**
 * Creates a vendor period object with calculated metrics
 */
function createVendorPeriod(
  vendor: number,
  startDate: Date,
  endDate: Date,
  transactions: KSTransaction[]
): VendorPeriod {
  const monthsDuration = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44); // Average month length
  const expectedFrequency = transactions.length / monthsDuration;

  const status = determineVendorStatus(transactions, monthsDuration);
  const confidenceScore = calculateConfidenceScore(transactions, monthsDuration);

  return {
    vendor,
    startDate,
    endDate,
    expectedFrequency,
    actualTransactions: transactions,
    status,
    confidenceScore,
  };
}

/**
 * Determines vendor status based on transaction pattern
 */
function determineVendorStatus(
  transactions: KSTransaction[],
  monthsDuration: number
): 'active' | 'inactive' | 'interrupted' {
  const lastTxnDate = new Date(Math.max(...transactions.map(t => t.date.getTime())));
  const daysSinceLastTxn = (new Date().getTime() - lastTxnDate.getTime()) / (1000 * 60 * 60 * 24);

  if (daysSinceLastTxn > 45) {
    return 'inactive';
  }

  // Check for significant gaps in the middle of the period
  const sortedTxns = [...transactions].sort((a, b) => a.date.getTime() - b.date.getTime());
  const hasInterruption = sortedTxns.some((txn, i) => {
    if (i === 0) return false;
    const gap = (txn.date.getTime() - sortedTxns[i - 1].date.getTime()) / (1000 * 60 * 60 * 24);
    return gap > 45;
  });

  return hasInterruption ? 'interrupted' : 'active';
}

/**
 * Calculates confidence score for the vendor period
 */
function calculateConfidenceScore(transactions: KSTransaction[], monthsDuration: number): number {
  // Factors that increase confidence:
  // 1. More transactions
  // 2. Regular intervals
  // 3. Consistent amounts
  const txnCountScore = Math.min(transactions.length / 10, 1);

  // Calculate regularity of intervals
  const sortedTxns = [...transactions].sort((a, b) => a.date.getTime() - b.date.getTime());
  const intervals = sortedTxns
    .slice(1)
    .map((txn, i) => (txn.date.getTime() - sortedTxns[i].date.getTime()) / (1000 * 60 * 60 * 24));

  const avgInterval = intervals.reduce((sum, int) => sum + int, 0) / intervals.length;
  const stdDevInterval = Math.sqrt(
    intervals.reduce((sum, int) => sum + Math.pow(int - avgInterval, 2), 0) / intervals.length
  );

  const regularityScore = Math.max(0, 1 - stdDevInterval / avgInterval);

  // Combine scores
  return txnCountScore * 0.4 + regularityScore * 0.6;
}

/**
 * Detects lifecycle anomalies for vendors
 */
export function detectVendorLifecycleAnomalies(
  transactions: KSTransaction[],
  periods: VendorPeriod[]
): VendorLifecycleAnomaly[] {
  const anomalies: VendorLifecycleAnomaly[] = [];

  periods.forEach(period => {
    // Detect start of service
    if (period.status === 'active') {
      anomalies.push({
        vendor: period.vendor,
        anomalyType: 'start',
        date: period.startDate,
        details: {
          newPattern: {
            frequency: period.expectedFrequency,
            typicalGap: calculateTypicalGap(period.actualTransactions),
          },
          confidenceScore: period.confidenceScore,
        },
      });
    }

    // Detect service interruptions
    if (period.status === 'interrupted') {
      const gaps = findSignificantGaps(period.actualTransactions);
      gaps.forEach(gap => {
        anomalies.push({
          vendor: period.vendor,
          anomalyType: 'interruption',
          date: gap.startDate,
          details: {
            previousPattern: {
              frequency: calculateFrequencyBeforeDate(period.actualTransactions, gap.startDate),
              typicalGap: calculateTypicalGapBeforeDate(period.actualTransactions, gap.startDate),
            },
            newPattern: {
              frequency: calculateFrequencyAfterDate(period.actualTransactions, gap.endDate),
              typicalGap: calculateTypicalGapAfterDate(period.actualTransactions, gap.endDate),
            },
            confidenceScore: period.confidenceScore,
          },
        });
      });
    }

    // Detect end of service
    if (period.status === 'inactive') {
      anomalies.push({
        vendor: period.vendor,
        anomalyType: 'stop',
        date: period.endDate,
        details: {
          previousPattern: {
            frequency: period.expectedFrequency,
            typicalGap: calculateTypicalGap(period.actualTransactions),
          },
          confidenceScore: period.confidenceScore,
        },
      });
    }
  });

  return anomalies;
}

// Helper functions for gap analysis
function calculateTypicalGap(transactions: KSTransaction[]): number {
  const sortedTxns = [...transactions].sort((a, b) => a.date.getTime() - b.date.getTime());
  const gaps = sortedTxns
    .slice(1)
    .map((txn, i) => (txn.date.getTime() - sortedTxns[i].date.getTime()) / (1000 * 60 * 60 * 24));
  return gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length;
}

function findSignificantGaps(transactions: KSTransaction[]): Array<{ startDate: Date; endDate: Date }> {
  const sortedTxns = [...transactions].sort((a, b) => a.date.getTime() - b.date.getTime());
  const gaps: Array<{ startDate: Date; endDate: Date }> = [];

  for (let i = 1; i < sortedTxns.length; i++) {
    const gap = (sortedTxns[i].date.getTime() - sortedTxns[i - 1].date.getTime()) / (1000 * 60 * 60 * 24);
    if (gap > 45) {
      // Significant gap threshold
      gaps.push({
        startDate: sortedTxns[i - 1].date,
        endDate: sortedTxns[i].date,
      });
    }
  }
  return gaps;
}

// Helper functions for frequency calculation
function calculateFrequencyBeforeDate(transactions: KSTransaction[], date: Date): number {
  const previousTxns = transactions.filter(t => t.date < date);
  if (previousTxns.length < 2) return 0;

  const monthsDuration = (date.getTime() - previousTxns[0].date.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
  return previousTxns.length / monthsDuration;
}

function calculateFrequencyAfterDate(transactions: KSTransaction[], date: Date): number {
  const subsequentTxns = transactions.filter(t => t.date > date);
  if (subsequentTxns.length < 2) return 0;

  const monthsDuration =
    (subsequentTxns[subsequentTxns.length - 1].date.getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
  return subsequentTxns.length / monthsDuration;
}

function calculateTypicalGapBeforeDate(transactions: KSTransaction[], date: Date): number {
  const previousTxns = transactions.filter(t => t.date < date);
  if (previousTxns.length < 2) return 0;

  const gaps = previousTxns
    .slice(1)
    .map((txn, i) => (txn.date.getTime() - previousTxns[i].date.getTime()) / (1000 * 60 * 60 * 24));
  return gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length;
}

function calculateTypicalGapAfterDate(transactions: KSTransaction[], date: Date): number {
  const subsequentTxns = transactions.filter(t => t.date > date);
  if (subsequentTxns.length < 2) return 0;
  const gaps = subsequentTxns
    .slice(1)
    .map((txn, i) => (txn.date.getTime() - subsequentTxns[i].date.getTime()) / (1000 * 60 * 60 * 24));
  return gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length;
}

// Example usage:
// const transactions = [...]; // Your transaction data
// const vendorPeriods = detectVendorPeriods(transactions);
// const lifecycleAnomalies = detectVendorLifecycleAnomalies(transactions, vendorPeriods);
